import { api } from "./api.js";
import { enrichSlot } from "../utils";

export const managementService = {
  // -----------------
  // Slots
  getAllSlots: async () =>
    api.get("/slots").then((r) => (r.data || []).map(enrichSlot)),
  getSlot: async (id) =>
    api.get(`/slots/${id}`).then((r) => enrichSlot(r.data)),
  getSlotFullDetails: async (id) =>
    api.get(`/slots/${id}/full-details`).then((r) => r.data),
  getScheduledSlots: async () =>
    api.get("/slots/scheduled").then((r) => (r.data || []).map(enrichSlot)),
  createSlot: (payload) =>
    api.post("/slots", payload).then((r) => enrichSlot(r.data)),
  updateSlot: (id, payload) =>
    api.put(`/slots/${id}`, payload).then((r) => enrichSlot(r.data)),
  cancelSlot: (id, payload) =>
    api
      .put(`/slots/${id}/cancel`, {
        cancellation_reason: payload.cancellation_reason,
      })
      .then((r) => enrichSlot(r.data)),
  deleteSlot: (id) => api.delete(`/slots/${id}`).then((r) => r.data),

  // -----------------
  // Events
  getAllEvents: () => api.get("/events").then((r) => r.data),
  getEvent: (id) => api.get(`/events/${id}`).then((r) => r.data),
  createEvent: (payload) => api.post("/events", payload).then((r) => r.data),
  updateEvent: (id, payload) =>
    api.put(`/events/${id}`, payload).then((r) => r.data),
  deleteEvent: (id) => api.delete(`/events/${id}`).then((r) => r.data),

  // -----------------
  // Participants
  // GET /participants?slot_id=123
  getParticipantsBySlot: (slotId) =>
    api
      .get("/participants", { params: { slot_id: slotId } })
      .then((r) => r.data),

  getAllParticipants: () => api.get("/participants").then((r) => r.data),

  getParticipant: (id) => api.get(`/participants/${id}`).then((r) => r.data),

  // POST /participants - Single participant (legacy)
  addParticipant: (payload) =>
    api.post("/participants", payload).then((r) => r.data),

  // POST /participants - Multiple participants with custom status
  addParticipants: (slotId, participants) =>
    api
      .post("/participants", {
        slot_id: slotId,
        slot_participants: participants, // [{ user_id, status }, ...]
      })
      .then((r) => r.data),

  // POST /participants - Multiple participants with same status (legacy)
  addParticipantsByIds: (slotId, userIds, status = "pending") =>
    api
      .post("/participants", {
        slot_id: slotId,
        user_ids: userIds,
        status: status,
      })
      .then((r) => r.data),

  // PUT /participants/:id
  updateParticipant: (id, payload) =>
    api.put(`/participants/${id}`, payload).then((r) => r.data),

  // PUT /participants/:id/status
  updateParticipantStatus: (id, status) =>
    api.put(`/participants/${id}/status`, { status }).then((r) => r.data),

  // DELETE /participants/:id
  removeParticipant: (id) =>
    api.delete(`/participants/${id}`).then((r) => r.data),

  // DELETE multiple participants
  removeParticipants: async (participantIds) => {
    return Promise.all(
      participantIds.map((id) => api.delete(`/participants/${id}`)),
    ).then((results) => results.map((r) => r.data));
  },

  // -----------------
  // Recurrences
  // GET /recurrences?event_id=X
  getAllRecurrences: (eventId = null) => {
    const params = eventId ? { event_id: eventId } : {};
    return api.get("/recurrences", { params }).then((r) => r.data);
  },
  getRecurrence: (id) => api.get(`/recurrences/${id}`).then((r) => r.data),
  createRecurrence: (payload) =>
    api.post("/recurrences", payload).then((r) => r.data),
  updateRecurrence: (id, payload) =>
    api.put(`/recurrences/${id}`, payload).then((r) => r.data),
  deleteRecurrence: (id) =>
    api.delete(`/recurrences/${id}`).then((r) => r.data),

  // -----------------
  // Event instructors
  getslotInstructors: async (eventId = null) => {
    const params = eventId ? { event_id: eventId } : {};
    return api.get("/instructors", { params }).then((r) => r.data);
  },

  assignInstructor: (payload) =>
    api.post("/instructors", payload).then((r) => r.data),

  assignInstructors: (slotId, instructorIds) =>
    api
      .post("/instructors", {
        slot_id: slotId,
        instructor_ids: instructorIds,
      })
      .then((r) => r.data),

  removeInstructor: (eventId, instructorId) =>
    api
      .delete(`/instructors/${eventId}/instructor/${instructorId}`)
      .then((r) => r.data),

  removeInstructors: async (eventId, instructorIds) => {
    return Promise.all(
      instructorIds.map((instructorId) =>
        api.delete(`/instructors/${eventId}/instructor/${instructorId}`),
      ),
    ).then((results) => results.map((r) => r.data));
  },
};

export default managementService;
